import { Alert, Button, Group, Modal, Stack, Text, TextInput, toast } from '@common/ui'
import { useCallback, useEffect, useRef, useState } from 'react'
import PermissionError from '../../PermissionError'
import EntityFieldInputGroup from '../shared/EntityFieldInputGroup'
import PageLayout from '../shared/PageLayout'
import useEntityEdit from '../shared/useEntityEdit'
import { KindEditPageProps } from '../KindPages'
import { getEntityLinkFromResourceKey, getResourceKeyFromEntity } from '../../../../io/entityResourceKeyLink'
import FileInput from '../../../../components/input/FileInput'
import computeLabel from '../shared/computeLabel'
import useUploadResource from '../../../../io/useUploadResource'
import useResource from '../../../../io/useResource'

const RuntimeEditPage = ({ entity, entityId, entityType, entityTypes, loading }: KindEditPageProps) => {
  const {
    fields,
    updateField,
    submitting,
    valid,
    submitEntity,
    deleteEntity,
    hasPermissions,
    permissionsLoading,
    registerPreSubmit,
  } = useEntityEdit({ entity, entityId, entityType })

  const { upload } = useUploadResource()

  const existingResourceId = entity ? getResourceKeyFromEntity(entity) : undefined
  const { isLoading: resourceLoading, error: resourceError } = useResource(existingResourceId)
  const fileRef = useRef<File | null>(null)
  const [fileSelected, setFileSelected] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [confirmationText, setConfirmationText] = useState('')

  const isReplacing = !!existingResourceId && fileSelected

  const handleFileChange = (file: File) => {
    fileRef.current = file
    setFileSelected(true)
  }

  const handleSave = () => {
    if (isReplacing) {
      setConfirmationText('')
      setDialogOpen(true)
    } else {
      submitEntity()
    }
  }

  const handleConfirm = () => {
    setDialogOpen(false)
    submitEntity()
  }

  const preSubmit = useCallback(async () => {
    const file = fileRef.current
    if (!file && !existingResourceId) return []
    if (!file) return []

    try {
      const resourceKey = await upload({ data: file, extension: 'js' })
      return [getEntityLinkFromResourceKey(resourceKey)]
    } catch (e) {
      console.error(e)
      toast.negative('Failed to upload runtime')
      throw e
    }
  }, [upload, existingResourceId])

  useEffect(() => {
    registerPreSubmit(preSubmit)
  }, [registerPreSubmit, preSubmit])

  if (!hasPermissions && !loading && !permissionsLoading) {
    return <PermissionError missingPermittedActions={[]} />
  }

  const label = computeLabel(entityType, fields)

  const requiredConfirmationText = 'backwards compatible'

  return (
    <>
      <PageLayout
        title={label ?? entityType.name}
        subtitle={label ? entityType.name : undefined}
        showCancel
        showSave
        showDelete={!!entity}
        saveDisabled={!valid || resourceLoading}
        submitting={submitting}
        onSave={handleSave}
        onDelete={deleteEntity}
      >
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingTop: 'var(--trellis-spacing-sm)' }}>
          <EntityFieldInputGroup
            entityType={entityType}
            entityTypes={entityTypes}
            fields={fields}
            updateField={updateField}
            loading={loading}
          />
          <Stack gap="xs" mt="md">
            <Text size="sm" fw={600}>
              Runtime script
            </Text>
            <FileInput onChange={handleFileChange} accept=".js" />
            {resourceError && <Alert severity="negative">Failed to load existing runtime script.</Alert>}
            {existingResourceId && !fileSelected && !resourceError && (
              <Text size="sm" c="dimmed">
                A runtime script is already uploaded for this entity. Select a new JavaScript file to replace it.
              </Text>
            )}
            {isReplacing && (
              <Alert severity="caution">
                Replacing a runtime affects all entities using it. Ensure backwards compatibility before proceeding.
              </Alert>
            )}
          </Stack>
        </div>
      </PageLayout>
      <Modal opened={dialogOpen} onClose={() => setDialogOpen(false)} title="Confirm runtime replacement">
        <Text mb="md">
          Replacing a runtime affects all entities using it. Type <strong>{requiredConfirmationText}</strong> to confirm
          the change won't break existing assets.
        </Text>
        <TextInput
          label="Confirmation"
          value={confirmationText}
          onChange={(e) => setConfirmationText(e.target.value)}
          size="sm"
        />
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={() => setDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} color="negative" disabled={confirmationText !== requiredConfirmationText}>
            Confirm
          </Button>
        </Group>
      </Modal>
    </>
  )
}

export default RuntimeEditPage
